<template>
    <el-dialog
        :visible.sync="dialogVisible"
        width="980px"
        center
        class="volc-voice-picker-dialog"
        @close="handleClose"
    >
        <div slot="title" class="volc-voice-picker-dialog__title">
            <i class="el-icon-headset"></i>
            <span>选择火山发音人</span>
        </div>

        <div class="volc-voice-picker-dialog__toolbar">
            <el-input
                v-model.trim="keyword"
                size="small"
                clearable
                placeholder="搜索发音人名称"
                class="volc-voice-picker-dialog__search"
            >
                <i slot="prefix" class="el-icon-search"></i>
            </el-input>
        </div>

        <div class="volc-voice-picker-dialog__summary">
            <span>共 {{ normalizedVoices.length }} 个发音人</span>
            <span>分类 {{ categoryTabs.length }} 组</span>
            <span v-if="currentVoiceName">当前：{{ currentVoiceName }}</span>
            <span>单击选中，点击右侧按钮试听，双击卡片可直接确认</span>
        </div>

        <div v-if="tabOptions.length" class="volc-voice-picker-dialog__body">
            <el-tabs v-model="activeCategory" stretch class="volc-voice-picker-dialog__tabs">
                <el-tab-pane
                    v-for="(tab, index) in tabOptions"
                    :key="`tab-${index}-${tab.value}`"
                    :label="tabLabel(tab)"
                    :name="tab.value"
                >
                    <div class="volc-voice-picker-dialog__grid">
                        <div
                            v-for="voice in tab.items"
                            :key="voice.value"
                            class="volc-voice-picker-card"
                            tabindex="0"
                            :class="{ 'volc-voice-picker-card--active': localSelectedValue === voice.value }"
                            @click="selectVoice(voice)"
                            @dblclick="confirmVoice(voice)"
                            @keydown.enter.prevent="confirmVoice(voice)"
                            @keydown.space.prevent="selectVoice(voice)"
                        >
                            <div class="volc-voice-picker-card__header">
                                <div
                                    class="volc-voice-picker-card__avatar"
                                    :class="voice.avatarClass"
                                    aria-hidden="true"
                                >
                                    <span>{{ voice.avatarText }}</span>
                                </div>
                                <div class="volc-voice-picker-card__main">
                                    <div class="volc-voice-picker-card__name">{{ voice.name }}</div>
                                    <div class="volc-voice-picker-card__meta">{{ voice.meta }}</div>
                                </div>
                                <button
                                    type="button"
                                    class="volc-voice-picker-card__preview"
                                    :class="{ 'volc-voice-picker-card__preview--playing': previewingVoiceKey === previewKey(voice) }"
                                    :disabled="!!previewLoadingKey && previewLoadingKey !== previewKey(voice)"
                                    @click.stop="emitPreview(voice)"
                                >
                                    <svg class="volc-voice-picker-card__progress" viewBox="0 0 44 44" aria-hidden="true">
                                        <circle class="volc-voice-picker-card__progress-track" cx="22" cy="22" r="18"></circle>
                                        <circle
                                            class="volc-voice-picker-card__progress-bar"
                                            cx="22"
                                            cy="22"
                                            r="18"
                                            :style="{ strokeDasharray: previewCircumference, strokeDashoffset: previewDashOffset(voice) }"
                                        ></circle>
                                    </svg>
                                    <span class="volc-voice-picker-card__preview-icon">
                                        <i v-if="previewLoadingKey === previewKey(voice)" class="el-icon-loading"></i>
                                        <template v-else-if="previewingVoiceKey === previewKey(voice)">
                                            <span class="volc-voice-picker-card__pause-bar"></span>
                                            <span class="volc-voice-picker-card__pause-bar"></span>
                                        </template>
                                        <i v-else class="el-icon-headset"></i>
                                    </span>
                                </button>
                            </div>

                            <div v-if="voice.badges.length" class="volc-voice-picker-card__chips">
                                <span
                                    v-for="badge in voice.badges"
                                    :key="`${voice.value}-${badge}`"
                                    class="volc-voice-picker-card__chip"
                                >
                                    {{ badge }}
                                </span>
                            </div>

                            <p class="volc-voice-picker-card__desc">{{ voice.description }}</p>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </div>

        <div v-else class="volc-voice-picker-dialog__empty">
            <i class="el-icon-info"></i>
            <span>当前筛选条件下没有找到可用发音人</span>
        </div>

        <div slot="footer" class="volc-voice-picker-dialog__footer">
            <span class="volc-voice-picker-dialog__footer-tip">优先播放预生成试听音频，缺失时会自动走实时合成兜底。</span>
            <div class="volc-voice-picker-dialog__footer-actions">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" :disabled="!selectedVoiceObject" @click="confirmCurrentSelection">确认使用</el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
const CATEGORY_ORDER = [
    '通用',
    '趣味',
    '新闻播报',
    '广告/短视频',
    '多语种场景',
    '超自然音色',
    '短视频玩法',
    '教育/陪伴'
]

export default {
    name: 'VolcVoicePickerDialog',
    props: {
        visible: { type: Boolean, default: false },
        voices: { type: Array, default: () => [] },
        selectedVoiceValue: { type: String, default: '' },
        currentVoiceName: { type: String, default: '' },
        previewingVoiceKey: { type: String, default: '' },
        previewLoadingKey: { type: String, default: '' },
        previewProgress: { type: Number, default: 0 }
    },
    data() {
        return {
            keyword: '',
            activeCategory: '__all__',
            localSelectedValue: ''
        }
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            }
        },
        normalizedVoices() {
            return (this.voices || []).map((item) => {
                const value = String((item && item.value) || '').trim()
                if (!value) return null
                const label = this.decodeHtmlEntities(String((item && item.label) || value).trim() || value)
                const category = this.decodeHtmlEntities(String((item && item.category) || '通用').trim() || '通用')
                const name = this.decodeHtmlEntities(String((item && item.name) || label).trim() || value)
                const sourceCategory = this.decodeHtmlEntities(String((item && item.sourceCategory) || '').trim())
                const meta = sourceCategory ? `${category} / ${sourceCategory}` : category
                const searchText = `${label} ${value} ${name} ${category} ${sourceCategory}`.toLowerCase()
                return {
                    value,
                    label,
                    name,
                    category,
                    sourceCategory,
                    meta,
                    searchText,
                    avatarText: this.getAvatarText(name),
                    avatarClass: this.getAvatarClass(sourceCategory || category),
                    badges: this.getVoiceBadges({
                        value,
                        label,
                        category,
                        sourceCategory,
                        previewReady: !!(item && item.previewReady)
                    }),
                    description: this.getVoiceDescription({ name, category, sourceCategory }),
                    previewText: String((item && item.previewText) || '').trim(),
                    previewUrl: String((item && item.previewUrl) || '').trim(),
                    previewReady: !!(item && item.previewReady)
                }
            }).filter(item => !!item)
        },
        keywordFilteredVoices() {
            const keyword = String(this.keyword || '').trim().toLowerCase()
            return this.normalizedVoices.filter((item) => {
                if (keyword && item.searchText.indexOf(keyword) < 0) return false
                return true
            })
        },
        categoryTabs() {
            const groupMap = new Map()
            this.keywordFilteredVoices.forEach((item) => {
                const category = String(item.category || '通用')
                if (!groupMap.has(category)) {
                    groupMap.set(category, {
                        value: category,
                        label: category,
                        items: []
                    })
                }
                groupMap.get(category).items.push(item)
            })
            const orderedCategories = Array.from(groupMap.keys()).sort((a, b) => this.categoryOrderIndex(a) - this.categoryOrderIndex(b))
            return orderedCategories.map(category => groupMap.get(category))
        },
        tabOptions() {
            if (!this.keywordFilteredVoices.length) return []
            return [{
                value: '__all__',
                label: '全部',
                items: this.keywordFilteredVoices
            }].concat(this.categoryTabs)
        },
        selectedVoiceObject() {
            const value = String(this.localSelectedValue || '').trim()
            if (!value) return null
            return this.normalizedVoices.find(item => item.value === value) || null
        },
        previewCircumference() {
            return 2 * Math.PI * 18
        }
    },
    watch: {
        visible(value) {
            if (!value) return
            this.keyword = ''
            this.localSelectedValue = String(this.selectedVoiceValue || '').trim()
            this.focusSelectedCategory()
        },
        selectedVoiceValue(value) {
            if (!this.visible) return
            this.localSelectedValue = String(value || '').trim()
            this.focusSelectedCategory()
        },
        tabOptions: {
            handler() {
                this.ensureActiveCategory()
            },
            immediate: true
        }
    },
    methods: {
        decodeHtmlEntities(value) {
            let decoded = String(value || '')
            if (!decoded || decoded.indexOf('&') < 0) return decoded
            for (let i = 0; i < 2; i += 1) {
                const next = this.decodeHtmlEntitiesOnce(decoded)
                if (next === decoded) break
                decoded = next
            }
            return decoded
        },
        decodeHtmlEntitiesOnce(value) {
            if (typeof document !== 'undefined') {
                const textarea = document.createElement('textarea')
                textarea.innerHTML = value
                return textarea.value
            }
            return String(value || '')
                .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
                .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
                .replace(/&amp;/g, '&')
        },
        categoryOrderIndex(category) {
            const idx = CATEGORY_ORDER.indexOf(String(category || '').trim())
            return idx >= 0 ? idx : CATEGORY_ORDER.length
        },
        ensureActiveCategory() {
            if (!this.tabOptions.length) {
                this.activeCategory = '__all__'
                return
            }
            if (!this.tabOptions.some(item => item.value === this.activeCategory)) {
                this.activeCategory = this.tabOptions[0].value
            }
        },
        focusSelectedCategory() {
            if (!this.tabOptions.length) {
                this.activeCategory = '__all__'
                return
            }
            const selected = this.selectedVoiceObject
            if (selected && this.tabOptions.some(item => item.value === selected.category)) {
                this.activeCategory = selected.category
                return
            }
            this.ensureActiveCategory()
        },
        tabLabel(tab) {
            if (!tab) return ''
            return `${tab.label} (${tab.items.length})`
        },
        previewKey(voice) {
            return voice && voice.value ? String(voice.value) : ''
        },
        previewDashOffset(voice) {
            const key = this.previewKey(voice)
            const progress = this.previewingVoiceKey === key ? Math.min(1, Math.max(0, Number(this.previewProgress || 0))) : 0
            return this.previewCircumference * (1 - progress)
        },
        getAvatarText(name) {
            const text = String(name || '').replace(/\s+/g, '').trim()
            if (!text) return '声'
            if (/^[A-Za-z]/.test(text)) return text.charAt(0).toUpperCase()
            return text.charAt(0)
        },
        getAvatarClass(category) {
            const text = String(category || '')
            if (text.indexOf('多语种') >= 0 || text.indexOf('英语') >= 0 || text.indexOf('日语') >= 0 || text.indexOf('葡语') >= 0 || text.indexOf('西语') >= 0) {
                return 'volc-voice-picker-card__avatar--multi'
            }
            if (text.indexOf('方言') >= 0 || text.indexOf('短视频玩法') >= 0) {
                return 'volc-voice-picker-card__avatar--dialect'
            }
            if (text.indexOf('超自然') >= 0) {
                return 'volc-voice-picker-card__avatar--special'
            }
            if (text.indexOf('新闻') >= 0) {
                return 'volc-voice-picker-card__avatar--news'
            }
            return 'volc-voice-picker-card__avatar--general'
        },
        getVoiceBadges(voice) {
            const badges = []
            const sourceCategory = String((voice && voice.sourceCategory) || '').trim()
            const label = String((voice && voice.label) || '')
            if (sourceCategory) badges.push(sourceCategory)
            if (/2\.0/i.test(label)) badges.push('2.0')
            if (label.indexOf('双语') >= 0) badges.push('双语')
            if (voice && voice.previewReady) badges.push('已预生成')
            return badges.slice(0, 3)
        },
        getVoiceDescription(voice) {
            const name = String((voice && voice.name) || '该发音人')
            const category = String((voice && voice.category) || '通用')
            if (category === '通用') return `${name}适合常规口播、通用旁白和大多数基础配音场景。`
            if (category === '趣味') return `${name}角色感更强，适合剧情演绎、个性表达和轻松有趣的内容。`
            if (category === '新闻播报') return `${name}适合新闻资讯、正式播报和强调信息传达的场景。`
            if (category === '广告/短视频') return `${name}适合带货口播、广告宣传、影视解说和短视频内容。`
            if (category === '多语种场景') return `${name}适合英语、日语等多语种展示，以及跨语种配音内容。`
            if (category === '超自然音色') return `${name}更接近真人说话状态，适合追求自然听感的语音内容。`
            if (category === '短视频玩法') return `${name}适合方言、动漫、说唱等更有记忆点和玩法感的表达。`
            if (category === '教育/陪伴') return `${name}适合儿童陪伴、知识讲解、客服提示和温和型内容。`
            return `${name}适合通用口播、解说和常规配音场景。`
        },
        selectVoice(voice) {
            this.localSelectedValue = voice && voice.value ? voice.value : ''
        },
        emitPreview(voice) {
            this.localSelectedValue = voice && voice.value ? voice.value : ''
            this.$emit('preview', voice)
        },
        confirmCurrentSelection() {
            if (!this.selectedVoiceObject) return
            this.confirmVoice(this.selectedVoiceObject)
        },
        confirmVoice(voice) {
            if (!voice || !voice.value) return
            this.localSelectedValue = voice.value
            this.$emit('confirm', {
                label: voice.label,
                value: voice.value,
                name: voice.name,
                category: voice.category,
                sourceCategory: voice.sourceCategory,
                previewText: voice.previewText,
                previewUrl: voice.previewUrl,
                previewReady: voice.previewReady
            })
        },
        handleClose() {
            this.$emit('close')
        }
    }
}
</script>

<style lang="less" scoped>
.volc-voice-picker-dialog__title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}

.volc-voice-picker-dialog__title i {
    margin-right: 8px;
    color: #409eff;
    font-size: 20px;
}

.volc-voice-picker-dialog__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
}

.volc-voice-picker-dialog__search {
    flex: 1 1 320px;
}

.volc-voice-picker-dialog__search :deep(.el-input__prefix) {
    display: flex;
    align-items: center;
    height: 100%;
}

.volc-voice-picker-dialog__search :deep(.el-input__prefix-inner) {
    display: inline-flex;
    align-items: center;
    height: 100%;
}

.volc-voice-picker-dialog__search :deep(.el-input__icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    line-height: 1;
}

.volc-voice-picker-dialog__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
    margin-bottom: 16px;
    color: #909399;
    font-size: 13px;
}

.volc-voice-picker-dialog__body {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 4px;
}

.volc-voice-picker-dialog__tabs :deep(.el-tabs__header) {
    margin-bottom: 16px;
}

.volc-voice-picker-dialog__tabs :deep(.el-tabs__nav-wrap::after) {
    background-color: #edf2f9;
}

.volc-voice-picker-dialog__tabs :deep(.el-tabs__item) {
    height: 42px;
    line-height: 42px;
    font-size: 14px;
    color: #606266;
}

.volc-voice-picker-dialog__tabs :deep(.el-tabs__item.is-active) {
    color: #409eff;
}

.volc-voice-picker-dialog__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
    gap: 14px;
}

.volc-voice-picker-card {
    border: 1px solid #e5eaf3;
    border-radius: 18px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    padding: 16px;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.volc-voice-picker-card:hover {
    border-color: #8ec5ff;
    box-shadow: 0 10px 24px rgba(64, 158, 255, 0.14);
    transform: translateY(-1px);
}

.volc-voice-picker-card--active {
    border-color: #409eff;
    box-shadow: 0 12px 28px rgba(64, 158, 255, 0.18);
}

.volc-voice-picker-card__header {
    display: flex;
    align-items: center;
    gap: 12px;
}

.volc-voice-picker-card__avatar {
    width: 54px;
    height: 54px;
    border-radius: 16px;
    flex: 0 0 54px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    font-weight: 700;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28);
}

.volc-voice-picker-card__avatar span {
    transform: translateY(-1px);
}

.volc-voice-picker-card__avatar--general {
    background: linear-gradient(135deg, #409eff 0%, #36cfc9 100%);
}

.volc-voice-picker-card__avatar--news {
    background: linear-gradient(135deg, #1d39c4 0%, #409eff 100%);
}

.volc-voice-picker-card__avatar--dialect {
    background: linear-gradient(135deg, #13c2c2 0%, #52c41a 100%);
}

.volc-voice-picker-card__avatar--multi {
    background: linear-gradient(135deg, #722ed1 0%, #eb2f96 100%);
}

.volc-voice-picker-card__avatar--special {
    background: linear-gradient(135deg, #ff7a59 0%, #ffb347 100%);
}

.volc-voice-picker-card__main {
    min-width: 0;
    flex: 1;
}

.volc-voice-picker-card__name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    line-height: 1.4;
    word-break: break-word;
}

.volc-voice-picker-card__meta {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
}

.volc-voice-picker-card__preview {
    position: relative;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #409eff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    padding: 0;
}

.volc-voice-picker-card__preview:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.volc-voice-picker-card__progress {
    position: absolute;
    inset: 0;
    width: 52px;
    height: 52px;
}

.volc-voice-picker-card__progress-track,
.volc-voice-picker-card__progress-bar {
    fill: none;
    stroke-width: 3.5;
}

.volc-voice-picker-card__progress-track {
    stroke: rgba(64, 158, 255, 0.18);
}

.volc-voice-picker-card__progress-bar {
    stroke: #409eff;
    stroke-linecap: round;
    transform: rotate(-90deg);
    transform-origin: 22px 22px;
    transition: stroke-dashoffset 0.12s linear;
}

.volc-voice-picker-card__preview-icon {
    position: relative;
    z-index: 1;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #ffffff;
    border: 1px solid #d8e9ff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
    transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.volc-voice-picker-card__preview:hover:not(:disabled) .volc-voice-picker-card__preview-icon,
.volc-voice-picker-card__preview--playing .volc-voice-picker-card__preview-icon {
    background: #409eff;
    color: #fff;
    border-color: #409eff;
}

.volc-voice-picker-card__pause-bar {
    width: 3px;
    height: 12px;
    border-radius: 999px;
    background: currentColor;
}

.volc-voice-picker-card__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
}

.volc-voice-picker-card__chip {
    max-width: 100%;
    padding: 5px 10px;
    border-radius: 999px;
    background: #f4f8ff;
    border: 1px solid #e4eefc;
    color: #606266;
    font-size: 12px;
    line-height: 1.4;
    word-break: break-word;
}

.volc-voice-picker-card__desc {
    margin: 12px 0 0;
    min-height: 44px;
    color: #5f6b7a;
    font-size: 13px;
    line-height: 1.7;
    word-break: break-word;
}

.volc-voice-picker-dialog__empty {
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #909399;
    font-size: 14px;
}

.volc-voice-picker-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.volc-voice-picker-dialog__footer-tip {
    color: #909399;
    font-size: 12px;
}

.volc-voice-picker-dialog__footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

@media (max-width: 768px) {
    .volc-voice-picker-dialog :deep(.el-dialog) {
        width: 94vw !important;
        margin-top: 4vh !important;
    }

    .volc-voice-picker-dialog__search {
        width: 100%;
        flex: 1 1 100%;
    }

    .volc-voice-picker-dialog__grid {
        grid-template-columns: 1fr;
    }

    .volc-voice-picker-card__header {
        align-items: flex-start;
    }

    .volc-voice-picker-card__desc {
        min-height: auto;
    }

    .volc-voice-picker-dialog__footer {
        flex-direction: column;
        align-items: stretch;
    }

    .volc-voice-picker-dialog__footer-tip {
        text-align: center;
    }

    .volc-voice-picker-dialog__footer-actions {
        justify-content: flex-end;
    }
}
</style>

<style lang="less">
.dark-mode .volc-voice-picker-dialog .el-dialog {
    background: var(--bg-card) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-dialog__title,
.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__name {
    color: var(--text-primary) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-dialog__summary,
.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__meta,
.dark-mode .volc-voice-picker-dialog .volc-voice-picker-dialog__footer-tip {
    color: var(--text-secondary) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__desc {
    color: var(--text-regular) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card {
    background: linear-gradient(180deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.98) 100%) !important;
    border-color: var(--border-color) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-dialog__tabs .el-tabs__item {
    color: var(--text-secondary) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-dialog__tabs .el-tabs__item.is-active {
    color: #69b1ff !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-dialog__tabs .el-tabs__nav-wrap::after {
    background-color: rgba(148, 163, 184, 0.18) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__chip {
    background: rgba(148, 163, 184, 0.1) !important;
    border-color: rgba(148, 163, 184, 0.18) !important;
    color: var(--text-regular) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__preview-icon {
    background: rgba(15, 23, 42, 0.88) !important;
    border-color: rgba(64, 158, 255, 0.28) !important;
    color: var(--text-primary) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__progress-track {
    stroke: rgba(125, 195, 255, 0.16) !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__progress-bar {
    stroke: #7dc3ff !important;
}

.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__preview:hover:not(:disabled) .volc-voice-picker-card__preview-icon,
.dark-mode .volc-voice-picker-dialog .volc-voice-picker-card__preview--playing .volc-voice-picker-card__preview-icon {
    background: #409eff !important;
    color: #fff !important;
    border-color: #409eff !important;
}
</style>
